import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SyncCryptService } from '../../../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../../../core/logger.service';
import { ERROR_CODES, ErrCode } from '../../../../shared/models';
import { environment } from '../../../../../environments/environment';
import { IStoreData, ITransferItemDownload } from 'src/sync';
import { DownloadChunkFetchService } from '../download-chunk-fetch.service';

/**
 * Persistent-worker download service for E2EE zip downloads.
 * Unlike DownloadWorkerService which creates a new Worker per chunk,
 * this service maintains a pool of reusable workers that load
 * crypto libraries once and stay alive across all file downloads.
 */
/** Per-generation, so a fetch from a cancelled pool keeps observing its own state. */
interface PoolCancellation {
  cancelled: boolean;
  signal: Subject<void>;
}

interface DecryptJob {
  resolve: (result: IStoreData) => void;
  reject: (err: Error | ErrCode) => void;
  tItem: ITransferItemDownload;
  response: ArrayBuffer;
  offset: number;
  reqLength: number;
  retry: number;
}

@Injectable({
  providedIn: 'root',
})
export class ZipDownloadWorkerService {
  private workerPool: Worker[] = [];
  private workerBusy: boolean[] = [];
  private pendingJobs: DecryptJob[] = [];
  private messageId = 0;
  private pendingMessages = new Map<
    number,
    { resolve: (result: IStoreData) => void; reject: (err: Error | ErrCode) => void }
  >();
  private cancellation: PoolCancellation = { cancelled: false, signal: new Subject<void>() };

  constructor(
    private crypt: SyncCryptService,
    private downloadChunkFetch: DownloadChunkFetchService,
    private log: LoggerService,
  ) {}

  /**
   * Initialize worker pool with the given size.
   * Call once before starting zip download.
   */
  public initPool(poolSize: number): void {
    this.destroyPool();
    this.resetCancellation();
    for (let i = 0; i < poolSize; i++) {
      const worker = new Worker(environment.jslibpath + 'zip-decrypt-worker.js');
      worker.addEventListener('message', (ev: MessageEvent) => {
        this.handleWorkerMessage(ev, i);
      });
      worker.addEventListener('error', (ev: ErrorEvent) => {
        this.handleWorkerError(ev, i);
      });
      this.workerPool.push(worker);
      this.workerBusy.push(false);
    }
  }

  /**
   * Destroy all workers in the pool. Call when zip download completes or is cancelled.
   */
  public destroyPool(): void {
    // Completes in-flight chunk fetches so their promises settle; unsubscribing alone would hang them.
    this.cancellation.cancelled = true;
    this.cancellation.signal.next();
    this.cancellation.signal.complete();

    for (const worker of this.workerPool) {
      try {
        worker.postMessage({ cmd: 'terminate' });
      } catch (error) {
        // Worker may already be closed
      }
    }
    this.workerPool = [];
    this.workerBusy = [];

    // Reject all pending promises so callers don't hang forever
    const cancelErr = new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
    for (const [, pending] of this.pendingMessages) {
      pending.reject(cancelErr);
    }
    this.pendingMessages.clear();
    for (const job of this.pendingJobs) {
      job.reject(cancelErr);
    }
    this.pendingJobs = [];
  }

  /**
   * Download and decrypt a chunk using a pooled worker.
   * Same interface as DownloadWorkerService.getChunk().
   */
  public async getChunk(
    tItem: ITransferItemDownload,
    offset: number,
    reqLength: number,
    retry = 0,
  ): Promise<IStoreData> {
    const cancellation = this.cancellation;
    const body = await this.downloadChunkFetch.fetchChunk(tItem, offset, reqLength, retry, {
      cancel$: cancellation.signal.asObservable(),
      isCancelled: () => cancellation.cancelled,
    });
    return this.decryptWithPool(tItem, body, offset, reqLength);
  }

  private resetCancellation(): void {
    this.cancellation = { cancelled: false, signal: new Subject<void>() };
  }

  private decryptWithPool(
    tItem: ITransferItemDownload,
    response: ArrayBuffer,
    offset: number,
    reqLength: number,
  ): Promise<IStoreData> {
    return new Promise<IStoreData>((resolve, reject) => {
      const job: DecryptJob = { resolve, reject, tItem, response, offset, reqLength, retry: 0 };
      const freeWorkerIdx = this.workerBusy.indexOf(false);
      if (freeWorkerIdx === -1) {
        this.pendingJobs.push(job);
        return;
      }
      this.dispatchToWorker(freeWorkerIdx, job);
    });
  }

  private dispatchToWorker(workerIdx: number, job: DecryptJob): void {
    this.workerBusy[workerIdx] = true;
    const id = ++this.messageId;
    this.pendingMessages.set(id, { resolve: job.resolve, reject: job.reject });

    this.workerPool[workerIdx].postMessage({
      id: id,
      cmd: 'decrypt',
      data: {
        response: job.response,
        tItem: job.tItem,
        jsliburl: environment.jslibpath,
        datakey: this.crypt.bytesToB64(job.tItem.data_key),
        offset: job.offset,
        reqLength: job.reqLength,
      },
    });
  }

  /** A dead worker posts no result, so fail its in-flight job instead of leaving the pool stalled. */
  private handleWorkerError(ev: ErrorEvent, workerIdx: number): void {
    this.log.error(`zip-decrypt-worker failed to run: ${ev.message}`);
    this.workerBusy[workerIdx] = false;

    const failure = new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR);
    for (const [id, pending] of this.pendingMessages) {
      this.pendingMessages.delete(id);
      pending.reject(failure);
    }
    for (const job of this.pendingJobs.splice(0)) {
      job.reject(failure);
    }
  }

  private handleWorkerMessage(ev: MessageEvent, workerIdx: number): void {
    const { id, cmd, data, errcode } = ev.data;
    const pending = this.pendingMessages.get(id);

    if (!pending) {
      return;
    }

    this.pendingMessages.delete(id);
    this.workerBusy[workerIdx] = false;

    if (cmd === 'result') {
      pending.resolve(data as IStoreData);
    } else if (cmd === 'error') {
      this.log.error(`Decrypt error: ${data?.errmsg}`);
      pending.reject(new ErrCode(errcode || 2050));
    }

    // Process next queued job if any
    if (this.pendingJobs.length > 0) {
      const job = this.pendingJobs.shift();
      if (job) {
        this.dispatchToWorker(workerIdx, job);
      }
    }
  }
}
